import { AdminChargeInvoiceConfig } from 'api/admin/charges/getCharges'

/**
 * Breakdown levels an invoice config can split on. Duplicated from the
 * InvoiceConfig page's schema on purpose — page internals are private, so
 * Charges keeps its own copy rather than importing across page modules.
 */
const BREAKDOWN_LEVEL_LABELS: Record<string, string> = {
  department: 'Department',
  location: 'Location',
  location_plus_name: 'Location + Name',
  position: 'Position',
  city: 'City',
  state: 'State',
  date: 'Date',
  shift: 'Shift',
}

const DATE_RANGE_PATTERN = /^\d{4}-\d{2}-\d{2} to \d{4}-\d{2}-\d{2}$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export const GROUP_KEY_TOOLTIP =
  'Charges that share every part of this key are collected onto the same invoice. Change any part and the charge lands on a different invoice.'

const GENERIC_PART_TOOLTIP =
  'Part of the invoice group key. Charges sharing the whole key bill together on one invoice.'

/**
 * Explain one part of an invoice group key.
 *
 * The backend returns the key as an opaque ordered list of strings, so parts are
 * identified by matching them against the invoice config rather than by
 * position — an unrecognised part gets a generic explanation instead of a
 * confidently wrong one.
 */
export function describeGroupKeyPart(
  part: string,
  config: AdminChargeInvoiceConfig
): string {
  if (config.companyGroupId != null && part === String(config.companyGroupId)) {
    return `Company group #${config.companyGroupId} — the billing scope this invoice config covers. Charges from every company in the group bill together.`
  }

  if (config.companyId != null && part === String(config.companyId)) {
    return `Company #${config.companyId} — the billing scope this invoice config covers.`
  }

  if (part === 'custom_week') {
    const startDay = config.customWeekStartDay
    const timezone = config.customWeekTimezone
    if (startDay != null) {
      return `Custom week — the billing period runs week to week starting ${startDay}${
        timezone != null ? ` (${timezone})` : ''
      }, rather than on the standard calendar week.`
    }
    return 'Custom week — the billing period follows the config’s custom week rather than the standard calendar week.'
  }

  if (part === config.cycleType) {
    return config.cycleType === 'weekly'
      ? 'Weekly cycle — one invoice is produced per week of charges.'
      : 'Standard cycle — invoices follow the company’s default billing cadence.'
  }

  if (DATE_RANGE_PATTERN.test(part)) {
    return 'Billing period — only charges whose shifts fall inside this window bill on this invoice.'
  }

  if (DATE_PATTERN.test(part)) {
    return 'Billing period date — charges are bucketed by this date.'
  }

  const breakdownLabel = BREAKDOWN_LEVEL_LABELS[part]
  if (breakdownLabel != null && config.breakdownLevels.includes(part)) {
    return `${breakdownLabel} breakdown — this config splits charges into a separate invoice per ${breakdownLabel.toLowerCase()}.`
  }

  if (config.breakdownLevels.length > 0) {
    return `${GENERIC_PART_TOOLTIP} This config breaks invoices down by ${config.breakdownLevels.join(
      ', '
    )}, so this is likely one of those values.`
  }

  return GENERIC_PART_TOOLTIP
}
